"use client";

import React, { useEffect, useRef, useState } from "react";

/* Leaf-ish content blocks only. `section`/`article` used to be in here, but a
   page-level container that fades takes the whole page with it: on the advisory
   detail pages the one <section> wrapping the body is 4800-7300px tall, never
   won its reveal on a phone, and left the page blank. */
const REVEAL_SELECTOR = "h1,h2,h3,p,ul,ol,table,pre,blockquote,hr,figure,.bordered-heading";

export default function SecurityAdvisoriesLayout({ children }: { children: React.ReactNode }) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Track mouse position for cursor glow
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    };

    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  // Auto scroll-reveal for common content blocks
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    // A decorative fade must never be the reason content is unreadable, so the
    // whole thing is opt-out for anyone who asked for less motion.
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const nodes = Array.from(root.querySelectorAll(REVEAL_SELECTOR)).filter(
      (n) => n instanceof HTMLElement
    ) as HTMLElement[];

    // Don’t animate tiny wrappers aggressively; focus on visible blocks.
    const candidates = nodes.filter((el) => {
      if (el.closest("svg")) return false;
      if (el.closest("button")) return false;
      if (el.dataset.saNoReveal === "true") return false;
      // ignore empty blocks
      const text = (el.textContent || "").trim();
      if (!text && el.tagName !== "HR") return false;
      return true;
    });

    // Nested targets stack their opacities and fade the inner text twice, so an
    // outer block yields to the blocks it contains.
    const targets = candidates.filter(
      (el) => !candidates.some((other) => other !== el && el.contains(other))
    );

    targets.forEach((el, idx) => {
      el.classList.add("sa-reveal-item");
      // Whatever is already on screen at mount renders straight away: no
      // first-paint flash, and nothing above the fold can be left behind if the
      // observer never fires.
      if (el.getBoundingClientRect().top <= window.innerHeight * 0.9) {
        el.classList.add("sa-reveal-show");
        return;
      }
      el.classList.add("sa-reveal-hidden");
      if (!el.style.transitionDelay) {
        el.style.transitionDelay = `${Math.min(idx, 16) * 35}ms`;
      }
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          if (!entry.isIntersecting) return;
          el.classList.add("sa-reveal-show");
          el.classList.remove("sa-reveal-hidden");
          io.unobserve(el);
        });
      },
      // The threshold must stay 0. It is a fraction of the *element's own* area,
      // so anything bigger than the viewport — or wider than the scroller that
      // clips it — can never reach a positive one and would stay at opacity 0
      // forever. The advisories table (1198px wide inside a 343px scroller on a
      // phone) topped out at 0.147 against the old 0.15 and never appeared.
      { root: null, threshold: 0, rootMargin: "0px 0px -10% 0px" }
    );

    targets.forEach((el) => {
      if (el.classList.contains("sa-reveal-hidden")) io.observe(el);
    });
    return () => io.disconnect();
  }, [children]);

  return (
    <div ref={containerRef} className="relative overflow-hidden">
      {/* Interactive cursor glow */}
      <div
        className="pointer-events-none absolute w-[320px] h-[320px] rounded-full opacity-0 hover:opacity-20 transition-opacity duration-300 z-[5]"
        style={{
          left: mousePos.x - 160,
          top: mousePos.y - 160,
          background: "radial-gradient(circle, rgba(236, 25, 56, 0.28) 0%, transparent 70%)",
          filter: "blur(44px)",
          transition: "left 0.1s ease-out, top 0.1s ease-out",
        }}
      />

      {/* Static grid background */}
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none sa-grid" />

      {/* Hexagonal grid pattern */}
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern
              id="sa-hexagons"
              width="50"
              height="43.4"
              patternUnits="userSpaceOnUse"
              patternTransform="scale(2)"
            >
              <polygon
                points="24.8,22 37.3,29.2 37.3,43.7 24.8,50.9 12.3,43.7 12.3,29.2"
                fill="none"
                stroke="rgba(236, 25, 56, 0.5)"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#sa-hexagons)" />
        </svg>
      </div>

      {/* Content */}
      <div ref={contentRef} className="relative z-10">
        {children}
      </div>

      <style jsx>{`
        .sa-grid {
          background-image:
            linear-gradient(rgba(236, 25, 56, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(236, 25, 56, 0.1) 1px, transparent 1px);
          background-size: 50px 50px;
        }

        /* Scroll-reveal */
        :global(.sa-reveal-item) {
          will-change: transform, opacity;
          transition-property: transform, opacity;
          transition-duration: 700ms;
          transition-timing-function: cubic-bezier(0.22, 1, 0.36, 1);
        }

        :global(.sa-reveal-hidden) {
          opacity: 0;
          transform: translateY(14px);
        }

        :global(.sa-reveal-show) {
          opacity: 1;
          transform: translateY(0);
        }

        /* Text interaction helpers (no per-page edits required) */
        :global(.sa-reveal-item a) {
          transition:
            color 250ms ease,
            text-shadow 250ms ease;
        }
        :global(.sa-reveal-item a:hover) {
          color: rgb(236, 25, 56);
          text-shadow: 0 0 10px rgba(236, 25, 56, 0.35);
        }
      `}</style>
    </div>
  );
}
